import type { Suggestion } from '@frontend/libs/types/search'

import { useSearchStore } from '@frontend/libs/stores/useSearchStore'
import { parseHtml } from '@frontend/libs/utils'
import { Button } from '@nextui-org/react'
import { __ } from '@wordpress/i18n'
import { useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import SearchHistoryList from './SearchHistoryList'

export interface SearchResultListProps {
  keyword: string
  onClearSearchHistory: () => void
  suggestions: Suggestion[]
}

export function SearchResultList({
  keyword,
  onClearSearchHistory,
  suggestions,
}: SearchResultListProps) {
  const navigate = useNavigate()
  const { addNewHistory, closeModal, histories } = useSearchStore(
    (state) => state,
  )

  const handleOnClickItem = useCallback(
    (item: Suggestion) => {
      if (item?.type === 'product') {
        addNewHistory(item)
      }
      if (item?.type === 'product' || item?.type === 'taxonomy') {
        const url = item.url.replace(window.location.origin, '')
        navigate(url)
      }
      closeModal()
    },
    [addNewHistory, closeModal, navigate],
  )

  if (!suggestions || !keyword) return <></>

  return (
    <div className="mx-auto h-[70dvh] w-full max-w-4xl overflow-auto">
      <div className="grid gap-2">
        {histories && histories.length > 0 && (
          <SearchHistoryList
            onClearSearchHistory={onClearSearchHistory}
            searchHistories={histories}
          />
        )}
        <div className="my-4 flex select-none items-center gap-2 border-b border-primary/20 pb-2">
          <h4 className="font-medium uppercase">
            {__('Search Result', 'asavagroup')}
          </h4>
        </div>
        {suggestions.map((item) => {
          if (item.type === 'no-results') {
            return (
              <div className="py-4 text-center uppercase" key={item.value}>
                No result found
              </div>
            )
          }
          if (item.type === 'headline') {
            return (
              <div
                className="my-4 select-none border-b border-primary/20 pb-2"
                key={item.value}
              >
                <h4 className="font-medium uppercase">
                  {item.value === 'tax_product_brand' && (
                    <span>{__('Brands', 'asavagroup')}</span>
                  )}
                  {item.value === 'tax_product_cat' && (
                    <span>{__('Categories', 'asavagroup')}</span>
                  )}
                  {item.value === 'product' && (
                    <span>{__('Products', 'asavagroup')}</span>
                  )}
                </h4>
              </div>
            )
          }
          if (item.type === 'taxonomy') {
            return (
              <Button
                className="block h-auto max-h-full py-2 text-left"
                fullWidth
                key={item.term_id}
                onPress={() => handleOnClickItem(item)}
                variant="light"
              >
                <div className="uppercase">
                  <span className="block">
                    <SearchItem item={item} keyword={keyword} />
                  </span>
                  <span className="block text-primary/60">
                    {item.breadcrumbs}
                  </span>
                </div>
              </Button>
            )
          }

          if (item.type === 'product') {
            return (
              <Button
                className="block h-auto max-h-full py-2 text-left"
                fullWidth
                key={item.post_id}
                onPress={() => handleOnClickItem(item)}
                variant="light"
              >
                <div className="flex items-center gap-4 uppercase">
                  {parseHtml(item.thumb_html)}
                  <span className="block">
                    <SearchItem item={item} keyword={keyword} />
                  </span>
                </div>
              </Button>
            )
          }
          if (item.type === 'more_products') {
            return (
              <Button
                as={Link}
                className="block h-auto max-h-full py-3 text-center uppercase"
                fullWidth
                key={item.total}
                to={`/shop/?s=${keyword}`}
                variant="light"
              >
                {__('See all products...', 'asavagroup')} ({item.total})
              </Button>
            )
          }
        })}
      </div>
    </div>
  )
}

export default SearchResultList

function SearchItem({
  item,
  keyword,
}: Readonly<{ item: { value: string }; keyword: string }>) {
  const highlightedText = useMemo(() => {
    const parts = item.value.split(new RegExp(`(${keyword})`, 'gi'))
    return parts.map((part, i) =>
      part.toLowerCase() === keyword.toLowerCase() ? (
        <b key={i}>{part}</b>
      ) : (
        part
      ),
    )
  }, [item.value, keyword])

  return <div>{highlightedText}</div>
}
